// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/QuestionForm.test.tsx
// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/QuestionFormAnswer.test.tsx
// covered-by: frontends/carrie/src/utils/__tests__/questionFormAnswerLine.test.ts

/** The selected-option text QuestionForm puts after `**Header:**`. */
export function questionOptionAnswerText(option: {
  label: string;
  value?: string;
}): string {
  // why: a blank value is "no value" on the wire — QuestionForm already skips it
  return option.value
    ? `${option.label} (selection: ${option.value})`
    : option.label;
}

/** One line of a question-form submit. The thread sends this string as the next message. */
export function formatQuestionFormAnswerLine(
  header: string,
  answer: string,
): string {
  return `**${header}:** ${answer}`;
}

/** Marks a continuation line so `parseQuestionFormAnswers` won't treat user text as a new header. */
export const FORM_ANSWER_CONTINUATION_MARK = "\u200b";

export function escapeFormAnswerContinuationLine(line: string): string {
  return `${FORM_ANSWER_CONTINUATION_MARK}${line}`;
}

export function isFormAnswerContinuationLine(rawLine: string): boolean {
  return rawLine.startsWith(FORM_ANSWER_CONTINUATION_MARK);
}

export function formAnswerContinuationPayload(rawLine: string): string {
  return rawLine.slice(FORM_ANSWER_CONTINUATION_MARK.length);
}

const WORDING_PREFIX = "Wording: ";
const FILES_PREFIX = "Files: ";

/** True when this raw wire line is QuestionForm's upload footer, not user prose. */
export function isFormUploadFooterLine(rawLine: string): boolean {
  return (
    !isFormAnswerContinuationLine(rawLine) &&
    rawLine.trim().startsWith(FILES_PREFIX)
  );
}

function displayFormAnswerLine(line: string): string {
  return isFormAnswerContinuationLine(line)
    ? formAnswerContinuationPayload(line).trim()
    : line;
}

/** First `Wording:` line QuestionForm appends from `question.question`. */
export function formWordingMarkerLine(storedQuestion: string): string {
  const firstLine = storedQuestion.trim().split("\n")[0];
  return `${WORDING_PREFIX}${firstLine}`;
}

function isFormWordingSuffixTail(
  lines: string[],
  wordingIndex: number,
  storedQuestion?: string,
): boolean {
  const wordingLine = lines[wordingIndex].trim();
  const stored = storedQuestion?.trim();
  // Stryker disable next-line ConditionalExpression: only the inner `wordingLine !== formWordingMarkerLine(stored)` -> true replacement is equivalent -- the only caller invokes this helper once `isFormMarker` (which compares the same wordingLine to formWordingMarkerLine(storedQuestion)) is already false, so whenever `stored` is truthy here the mismatch already holds and forcing it true changes nothing. The whole-condition replacements are NOT equivalent: -> true is killed by "strips a Wording suffix tail of continuation lines and Files footer" and "keeps answer lines that follow a stripped upload footer block"; -> false is killed by "keeps user Wording prose and drops only the Files footer"
  if (stored && wordingLine !== formWordingMarkerLine(stored)) {
    // why: a person can type "Wording: …" in free text; Files after that is
    // only an upload footer, not a form marker block to hide.
    return false;
  }
  let index = wordingIndex + 1;
  while (index < lines.length) {
    const line = lines[index];
    if (isFormUploadFooterLine(line)) return true;
    if (isFormAnswerContinuationLine(line)) {
      index += 1;
      continue;
    }
    return false;
  }
  return false;
}

function stripTrailingUploadFooter(lines: string[]): string[] {
  const kept = [...lines];
  while (kept.length > 0) {
    const raw = kept[kept.length - 1];
    if (isFormUploadFooterLine(raw)) {
      kept.pop();
      continue;
    }
    break;
  }
  return kept;
}

function joinDisplayLines(lines: string[]): string {
  return stripTrailingUploadFooter(lines).map(displayFormAnswerLine).join("\n");
}

function stripFormWordingSuffixBlock(
  lines: string[],
  wordingIndex: number,
): string {
  const kept = lines.slice(0, wordingIndex).map(displayFormAnswerLine);
  let index = wordingIndex + 1;
  while (index < lines.length) {
    const line = lines[index];
    if (isFormUploadFooterLine(line)) {
      index += 1;
      break;
    }
    index += 1;
  }
  while (index < lines.length) {
    kept.push(displayFormAnswerLine(lines[index]));
    index += 1;
  }
  return kept.join("\n");
}

/** Wire answers may carry a form `Wording:` suffix; the answer card hides that block only. */
export function questionFormAnswerDisplayText(
  answer: string,
  options?: { storedQuestion?: string },
): string {
  const lines = answer.split("\n");
  const wordingIndex = lines.findIndex((line) =>
    line.trim().startsWith(WORDING_PREFIX),
  );
  // Stryker disable next-line EqualityOperator: only the `<= 0` replacement is equivalent -- when wordingIndex is 0, the `wordingIndex > 0` guard further down always keeps the marker-stripping branch off, so the function falls through to joinDisplayLines(lines) either way. The `>= 0` replacement is NOT equivalent: it returns early for every answer that has a Wording line (skipping marker stripping) and lets answers with none fall through, killed by "strips the form marker Wording block when storedQuestion matches" and several other tests in this file
  if (wordingIndex < 0) {
    return joinDisplayLines(lines);
  }
  // why: a free-text reply can be exactly "Wording: …"; that is the answer, not a hidden marker.
  // Stryker disable next-line ConditionalExpression,EqualityOperator: only the `wordingIndex === 0` replacements (-> true, -> !== 0) and the whole check -> false are equivalent -- a one-line array has no index but 0, and when the real check is true joinDisplayLines(lines) returns that identical single line, so a forced-false path falls through to the same string. The `lines.length === 1` replacements are NOT equivalent: -> true and -> !== 1 are both killed by "still strips a trailing upload footer when the lone Wording line opens a multi-line answer" and "does not strip content after a Wording line that is the very first line". The whole check -> true is NOT equivalent either: it returns `answer` for every answer with a Wording line and skips marker stripping, killed by "strips the form marker Wording block when storedQuestion matches", "strips a Wording suffix tail of continuation lines and Files footer", "still strips a trailing upload footer when the lone Wording line opens a multi-line answer" and "does not strip content after a Wording line that is the very first line"
  if (wordingIndex === 0 && lines.length === 1) {
    return answer;
  }
  const wordingLine = lines[wordingIndex].trim();
  const storedQuestion = options?.storedQuestion?.trim();
  const isFormMarker =
    storedQuestion != null &&
    // Stryker disable next-line ConditionalExpression,EqualityOperator: only the `>= 0` and `true` replacements are equivalent -- storedQuestion.length > 0 only differs from them when storedQuestion is "" after trim, and formWordingMarkerLine("") always ends in WORDING_PREFIX's trailing space, which the already-trimmed wordingLine above can never equal, so the marker comparison below stays false either way. The `<= 0` and `false` replacements are NOT equivalent: both are killed by "drops plain suffix lines after a matched form marker until the upload footer"
    storedQuestion.length > 0 &&
    wordingLine === formWordingMarkerLine(storedQuestion);
  if (
    wordingIndex > 0 &&
    (isFormMarker ||
      isFormWordingSuffixTail(lines, wordingIndex, storedQuestion))
  ) {
    return stripFormWordingSuffixBlock(lines, wordingIndex);
  }
  return joinDisplayLines(lines);
}

export function wireMultilineFormAnswerBody(text: string): string {
  const lines = text.split("\n");
  return lines
    .map((line, index) => {
      if (index > 0) return escapeFormAnswerContinuationLine(line);
      if (line.trim().startsWith(FILES_PREFIX)) {
        return escapeFormAnswerContinuationLine(line);
      }
      return line;
    })
    .join("\n");
}
